import {ArrowDownIcon, Box, Typography} from '@customink/pigment-react';
import {styled} from '@customink/pigment-react/lib/themeCustomink';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';

const OuterContainer = styled(Box)({
  paddingTop: {md: '0.5rem', sm: 0}
});

const ScrollableContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded'
})(({isExpanded}) => ({
  ...(isExpanded
    ? {
        display: 'grid',
        gap: '0.5rem',
        gridTemplateColumns: 'repeat(auto-fill, minmax(2rem, 1fr))',
        paddingBottom: '0.25rem'
      }
    : {
        '&::-webkit-scrollbar': {display: 'none'},
        alignItems: 'center',
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'flex-start',
        marginLeft: '-1rem',
        marginRight: '-1rem',
        minHeight: 'fit-content',
        msOverflowStyle: 'none',
        overflowX: 'auto',
        overflowY: 'visible',
        paddingBottom: '0.25rem',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        scrollbarWidth: 'none'
      })
}));

const ToggleButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '0.5rem'
});

const ToggleButton = styled(Box)({
  '&:active, &:focus, &:hover': {backgroundColor: 'transparent'},
  alignItems: 'center',
  color: 'rgba(0, 0, 0, 0.57)',
  cursor: 'pointer',
  display: 'flex',
  fontSize: '0.875rem',
  lineHeight: '125%',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  outline: 'none',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  WebkitUserSelect: 'none',
  whiteSpace: 'nowrap'
});

const ArrowIconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded'
})(({isExpanded}) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  lineHeight: 1,
  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.1s ease',
  width: 'fit-content'
}));

const ButtonText = styled(Typography)({
  color: 'inherit',
  fontSize: '0.875rem'
});

export default function ProductScrollableColorSwatchesContainer({
  children,
  totalColorCount
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const scrollContainerRef = useRef(null);

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const checkOverflow = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const isOverflowing = container.scrollWidth > container.clientWidth;
    setHasOverflow(isOverflowing);
  }, []);

  // Check overflow on mount, resize, and children change
  useEffect(() => {
    // Initial check with small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkOverflow, 50);

    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow, children]);

  const ariaLabel = isExpanded
    ? 'See less colors'
    : `See all ${totalColorCount} colors`;
  const buttonText = isExpanded ? 'See less' : `See all ${totalColorCount}`;

  return (
    <OuterContainer>
      <ScrollableContainer ref={scrollContainerRef} isExpanded={isExpanded}>
        {children}
      </ScrollableContainer>
      {hasOverflow && (
        <ToggleButtonContainer>
          <ToggleButton
            onClick={handleToggle}
            role="button"
            aria-expanded={isExpanded}
            aria-label={ariaLabel}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
            }}>
            <ButtonText>{buttonText}</ButtonText>
            <ArrowIconContainer isExpanded={isExpanded}>
              <ArrowDownIcon sx={{height: '1.3rem', width: '1.3rem'}} />
            </ArrowIconContainer>
          </ToggleButton>
        </ToggleButtonContainer>
      )}
    </OuterContainer>
  );
}

ProductScrollableColorSwatchesContainer.propTypes = {
  children: PropTypes.node.isRequired,
  totalColorCount: PropTypes.number.isRequired
};
